import type { ChannelId } from './channel.js';
// ENG-7025 / ADR-0032: the agent's kind ('standard' | 'system_support') is the
// single source of truth in the support-agent module. Type-only import keeps
// this a compile-time edge (no runtime cycle with the generation layer).
import type { SupportAgentKind } from '../generation/support-agent.js';

export type Environment = 'dev' | 'stage' | 'prod';
export type RiskTier = 'Low' | 'Medium' | 'High';
export type AgentStatus = 'draft' | 'active' | 'paused' | 'revoked';
export type ReportsToType = 'agent' | 'person';

/**
 * ENG-5144 / ENG-5151 / ADR-0032: agent statuses that occupy a host slot. Only
 * `active` counts — `draft` is still being authored in the recruitment wizard,
 * `paused` is how an operator frees a slot ahead of a migration, and `revoked`
 * is decommissioned (row retained for audit).
 *
 * This is HALF of the capacity predicate. The other half is the kind filter
 * `agent_kind <> SYSTEM_SUPPORT_AGENT_KIND` (generation/support-agent.ts) —
 * platform-internal support agents never occupy a customer slot. Both mirror
 * `_check_host_capacity` (migration `20260517000003_host_capacity_active_only`)
 * and must move in lock-step with it.
 *
 * ENG-8836 lifted this into core because the count has more than one consumer:
 * the API (`packages/api/src/lib/host-capacity.ts`, which re-exports this) and
 * the webapp's host list. A host list that counts every `host_agents` row
 * disagrees with the capacity check that governs whether an agent can actually
 * be placed — so the two must read one constant, not two copies.
 */
export const DEPLOYED_AGENT_STATUSES = ['active'] as const satisfies readonly AgentStatus[];

/**
 * Framework runtimes an agent can run under. ENG-6932 (hard removal, following
 * the ENG-6919 soft deprecation) removed OpenClaw, NemoClaw and the Anthropic
 * Managed Agents adapters, leaving claude-code as the only framework.
 *
 * ADR-0046 reintroduces `opencode` (https://opencode.ai) as a second, supported
 * framework - primarily to enable channel-enabled hosts running cheap
 * non-Anthropic models with no Anthropic account dependency (a capability
 * claude-code structurally cannot offer, since its native channels are gated on
 * a claude.ai login). claude-code remains the default; opencode is opt-in.
 */
export type FrameworkId = 'claude-code' | 'opencode';

/** The default framework for newly provisioned agents and hosts (opencode is opt-in). */
export const DEFAULT_FRAMEWORK: FrameworkId = 'claude-code';

/**
 * Source of truth for framework deprecation. `satisfies Record<FrameworkId, …>`
 * keeps this map in lockstep with the FrameworkId union. Neither supported
 * framework is deprecated; the map and the predicate are retained so legacy
 * framework strings on historical rows resolve to "not deprecated" without
 * throwing.
 */
export const FRAMEWORK_DEPRECATION = {
  'claude-code': false,
  'opencode': false,
} as const satisfies Record<FrameworkId, boolean>;

/** True when `id` is a known, deprecated framework. Unknown ids are treated as not deprecated. */
export function isDeprecatedFramework(id: string): boolean {
  return (FRAMEWORK_DEPRECATION as Record<string, boolean>)[id] === true;
}

/**
 * ENG-9485 (ADR-0074 open question 9) — the model-map SLOT names each framework
 * can consume. This is the answer to "what does a `model_policy_models` row
 * key mean?", and it deliberately does NOT live in a database CHECK.
 *
 * WHY NOT A CHECK. §2 keyed the map on `tier ('opus'|'sonnet'|'haiku')`,
 * reasoning from three env vars Claude Code was believed to read
 * independently. One of the three — `ANTHROPIC_DEFAULT_OPUS_MODEL` — is set
 * NOWHERE in this tree, so the vocabulary was derived from an API we do not
 * call. Worse, it is Anthropic-shaped, while `FrameworkId` already carries
 * `opencode`, whose models are named `provider/model` (`toOpencodeModel`,
 * frameworks/opencode/config.ts) and never as a Claude family. A CHECK is the
 * one place that cannot branch on framework, so encoding one framework's
 * vocabulary there makes the map mean something only for the table it was born
 * beside — which is `hosts.claude_auth_mode`'s defect one level up, and the
 * defect this epic exists to remove.
 *
 * WHAT A SLOT IS. The name of a position the framework's runtime fills, not a
 * capability class and not a model family. `claude-code` fills three; two are
 * the ones the launcher wires as env (`ANTHROPIC_MODEL`,
 * `ANTHROPIC_SMALL_FAST_MODEL` — persistent-session.ts) and the third is
 * `advisor` (see below). `opencode` has ONE model slot and no small-fast
 * concept at all (buildOpencodeConfig emits a single `model` + `provider`).
 *
 * ENG-9811 IS THE FIRST TEST OF THE "COSTS A LINE, NOT A MIGRATION" CLAIM, AND
 * IT PASSES. `advisor` was added by extending the array below: no CHECK to
 * widen (the only DB constraint on `slot` is non-blank —
 * `model_policy_models_slot_not_blank`, migration 20260901000003), no PK
 * change, no revision bump, and therefore no fleet-wide respawn. Had this
 * arrived before ENG-9485 keyed the map on framework-owned slot names, it
 * would have been a migration against a live PK carrying a revision trigger.
 *
 * `advisor` IS NOT AN ENV VAR, WHICH MAKES IT THE FIRST SLOT WITH ITS OWN
 * ACTUATOR. There is no `ANTHROPIC_ADVISOR_MODEL`; Claude Code reads
 * `advisorModel` from settings (or `--advisor` / `--settings` on argv).
 * MEASURED on CLI 2.1.261 (docs/research/eng-9811-advisor-model-delivery.md):
 * `<projectDir>/.claude/settings.json` IS honoured, and it OVERRIDES user-level
 * settings — so the actuator is agent-grain and fail-soft, not the host-grain
 * trap that `hosts.claude_auth_mode` was. ENG-10075 built it:
 * `writeProjectClaudeSettings` (apps/cli/src/lib/project-claude-settings.ts),
 * called from the spawner.
 *
 * ENG-10075 CORRECTION: this sentence used to say that file was "the per-agent
 * PROJECT .claude/settings.json the claude-code adapter already generates". The
 * adapter generates a `settings.json`, and it is NOT that one — artefacts are
 * materialised at `join(agentDir, relativePath)`, so the adapter's lands at
 * `<agentDir>/settings.json`, which Claude Code never reads (ENG-5631 recorded
 * the same thing about its own inert `model` field). Building to the old wording
 * would have written `advisorModel` where nothing reads it: a policy row set, a
 * file written, and no advisor — the silent-attach failure the slot's own
 * rollout note warns about.
 *
 * `advisor` IS ALSO THE FIRST SLOT THAT CAN BE INVALID FOR A BINDING rather
 * than merely unconsumed. The advisor is a server-executed tool, and Claude
 * Code refuses to attach it unless its auth resolves to first-party — so a
 * `bedrock` binding can never carry one. `model-policy-validate.ts` rejects
 * that pairing at write; the `gateway` case is deliberately NOT rejected,
 * because it is unmeasured and belongs to ENG-9487's capability probe.
 *
 * WHAT A SLOT IS NOT. It is not the alias an agent picks. `agents.primary_model`
 * keeps holding `fable | opus | opus[fast] | sonnet | haiku` for claude-code and
 * a `provider/model` id for opencode — it is already polymorphic by framework
 * and stays that way. In particular `opus[fast]` must never become a slot:
 * `[fast]` is a runtime MODE toggled by a slash command after boot (see
 * CLAUDE_CODE_MODEL_OPTIONS in types/models.ts), so a slot for it would carry a
 * `model_id` identical to `opus`'s while the thing it actually controls lives
 * on a different actuator entirely.
 *
 * `satisfies Record<FrameworkId, …>` keeps this in lockstep with the union, so
 * a new framework cannot be added without stating its slots — mirroring
 * FRAMEWORK_DEPRECATION above.
 */
export const FRAMEWORK_MODEL_SLOTS = {
  'claude-code': ['primary', 'small_fast', 'advisor'],
  'opencode': ['primary'],
} as const satisfies Record<FrameworkId, readonly string[]>;

/**
 * Slots whose runtime is a SERVER-EXECUTED tool, and therefore cannot be filled
 * under a binding that does not resolve to first-party Anthropic auth.
 *
 * ENG-9811. Every slot before `advisor` was an ordinary model id that any
 * transport could carry, which is why the docblock above says an unconsumed
 * slot "is not an error, merely unconsumed". That stops being true here: an
 * `advisor` row under a `bedrock` binding is not unconsumed, it is impossible,
 * and left unchecked it is impossible SILENTLY — the policy names a model, the
 * host attaches nothing, and billing and behaviour look normal.
 *
 * Derived from, and kept in one place for, `model-policy-validate.ts`. It is
 * NOT a capability ranking: which model may advise which is versioned,
 * asymmetric and resolved by the host's own Claude Code build, so encoding it
 * centrally would be the same grain error one level down.
 */
export const SERVER_EXECUTED_MODEL_SLOTS: ReadonlySet<string> = new Set(['advisor']);

/**
 * Every slot name any registered framework can consume. This is the set a
 * policy WRITE is validated against; which subset actually applies is decided
 * per agent at resolution time by that agent's framework.
 *
 * Derived, never hand-listed: a second literal list would be the ENG-8231
 * mirrored-allowlist failure, where a name present on one side and absent on
 * the other is dropped with no error.
 */
export const KNOWN_MODEL_SLOTS: ReadonlySet<string> = new Set(
  Object.values(FRAMEWORK_MODEL_SLOTS).flat(),
);

/**
 * The slots `framework` can consume. An unknown framework string (a legacy row)
 * yields an EMPTY list rather than a guess — the caller decides what to do with
 * "this framework consumes no policy models", and no caller may read the empty
 * list as "every slot applies".
 */
export function modelSlotsForFramework(framework: string): readonly string[] {
  return (FRAMEWORK_MODEL_SLOTS as Record<string, readonly string[]>)[framework] ?? [];
}

/**
 * Plan-tier bucket for an agent: `standard` (non-coding) or `advanced` (coding).
 * Counted against `plans.max_standard_agents` / `max_advanced_agents` by the
 * `enforce_org_agent_cap` trigger. Distinct from `agent_kind` (a host-capacity
 * exemption flag) — these are orthogonal.
 */
export const AGENT_CLASSES = ['standard', 'advanced'] as const;
export type AgentClass = (typeof AGENT_CLASSES)[number];

/** The default class for newly provisioned agents (advanced is opt-in). */
export const DEFAULT_AGENT_CLASS: AgentClass = 'standard';

/** Narrow an arbitrary string to a valid AgentClass, falling back to the default. */
export function normalizeAgentClass(value: unknown): AgentClass {
  return AGENT_CLASSES.includes(value as AgentClass)
    ? (value as AgentClass)
    : DEFAULT_AGENT_CLASS;
}

/**
 * ENG-10641 (ADR-0035, amended): the Docker `--memory` ceiling for an isolated
 * agent's container, by class. `advanced` is the coding class, and coding agents
 * build and test code: the `@augmented/api` typecheck alone peaks at ~5,120 MiB
 * (ENG-9673), which a 2 GiB container OOM-kills. 6g is that measurement plus the
 * claude + MCP working set.
 *
 * A CEILING, NOT A RESERVATION. Docker reserves nothing at `--memory`, so these
 * values do not have to sum to host RAM and carry no host-capacity weight. Only
 * what an agent actually uses competes for memory.
 *
 * Values use Docker's size syntax because they are passed straight to
 * `docker run --memory` and `docker update --memory`.
 */
export const AGENT_CLASS_CONTAINER_MEMORY: Record<AgentClass, string> = {
  standard: '2g',
  advanced: '6g',
};

/**
 * The container memory ceiling for a (possibly unvalidated) `agent_class`. An
 * unknown or missing class resolves to `standard`, so a stale API that does not
 * deliver the field keeps every agent on today's 2g.
 *
 * Pure: the operator override (`AGT_ISOLATION_MEMORY`) is applied by the CLI, not
 * here.
 */
export function containerMemoryForAgentClass(value: unknown): string {
  return AGENT_CLASS_CONTAINER_MEMORY[normalizeAgentClass(value)];
}

export interface AgentStandup {
  yesterday: string;
  today: string;
  blockers: string;
  updated_at: string;
}

export type AuthProfileType = 'api_key' | 'oauth';

export interface AgentAuthProfile {
  id: string;
  agent_id: string;
  team_id: string;
  provider: string;
  profile_name: string;
  auth_type: AuthProfileType;
  api_key?: string;
  metadata: Record<string, unknown>;
  created_by: string;
  created_at: string;
  updated_at: string;
}

/**
 * ENG-7854: the ONE canonical display_name -> code_name derivation, shared by
 * both recruitment wizards and the server-side re-derivation in
 * POST /agents/create-full. Produces strict kebab-case matching the charter
 * frontmatter pattern (^[a-z0-9]+(-[a-z0-9]+)*$) and the admin-debug codename
 * allowlists - underscores and every other non-alphanumeric collapse to a
 * single dash. Returns '' when the name has no usable characters (callers
 * must reject that). Divergent per-wizard slugify copies caused the
 * display/codename splits this replaces; add new call sites against this,
 * never a local copy.
 */
export function deriveAgentCodeName(displayName: string): string {
  return displayName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export interface Agent {
  agent_id: string;
  team_id: string;
  code_name: string;
  display_name: string;
  description?: string;
  role?: string | null;
  created_by: string;
  owner: string;
  environment: Environment;
  risk_tier: RiskTier;
  status: AgentStatus;
  /**
   * ENG-7025 / ADR-0032: agent kind. Defaults to 'standard'; 'system_support'
   * marks the auto-provisioned per-org Augmented Support agent. Optional here so
   * historical rows read before the column existed still type-check.
   */
  agent_kind?: SupportAgentKind;
  /**
   * Plan-tier bucket: 'standard' (non-coding) or 'advanced' (coding). Defaults
   * to 'standard'. Counted against the org plan's max_standard_agents /
   * max_advanced_agents. Optional here so historical rows read before the
   * column existed still type-check.
   */
  agent_class?: AgentClass;
  /**
   * ENG-5561: why the agent is currently paused ('manual', 'hourly_cost_exceeded').
   * Set when status flips to paused, cleared on resume. NULL when active or
   * paused by a pre-column path — status is the source of truth for paused-ness.
   */
  paused_reason?: string | null;
  framework: FrameworkId;
  /** Anthropic Managed Agents API agent ID — set when framework='managed-agents' and synced */
  anthropic_agent_id?: string | null;
  /** Anthropic Managed Agents API environment ID — set at sync time */
  anthropic_environment_id?: string | null;
  session_mode: 'oneshot' | 'persistent';
  charter_version_id?: string;
  tools_version_id?: string;
  budget_tokens_per_day?: number;
  budget_dollars_per_month?: number;
  /**
   * ENG-5559: per-agent USD/hr cost ceiling for the hourly cost guardrail
   * (ENG-5556). null/undefined = inherit team → global default. Most-specific
   * scope wins; resolved by resolveHourlyCostLimitUsd().
   */
  hourly_cost_limit_usd?: number | null;
  avatar_url?: string;
  /**
   * ENG-5733: optional 128×128 thumbnail URL, generated alongside the
   * 512×512 avatar_url. UI components render this in lists / cards for
   * fast loads; downloads (Slack profile picture export, modal preview)
   * still consume avatar_url.
   */
  avatar_thumb_url?: string;
  channels: ChannelId[];
  reports_to?: string | null;
  reports_to_type?: ReportsToType;
  last_heartbeat_at?: string | null;
  personality_seed?: string | null;
  primary_model?: string | null;
  secondary_model?: string | null;
  tertiary_model?: string | null;
  /**
   * ENG-6695: per-agent IANA timezone override for restart-deferral /
   * maintenance-window scheduling. `null` = inherit (team → org → UTC); see
   * `resolveAgentTimezone`.
   */
  timezone?: string | null;
  standup?: AgentStandup | null;
  current_tasks?: string | null;
  diagnostics?: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}
